import { useTranslation } from 'react-i18next';
import { useEntity, useEntityFinancials } from '../../api/hooks.js';
import { PanelShell } from '../panel-shell.js';
import { ScopeBadge } from '../scope-badge.js';
import { usePanelEntityId, type PanelId, type TabId } from '../store.js';

// Three-year financial snapshot. Reads from /entities/:id/financials.
// Honours scope flags — for `screener_only` entities the API returns an
// empty array and we surface the "no financial data" state.
//
// Numbers are rendered with `.num` to enable tabular-nums via tokens.css.
// Charts (trend sparkline) come in sub-unit D with d3.
export function FinancialSnapshotPanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const { t } = useTranslation();
  const entityId = usePanelEntityId(tabId, panelId);
  const entityQuery = useEntity(entityId);
  const financialsQuery = useEntityFinancials(entityId);

  const loading = !!entityId && (financialsQuery.isLoading || entityQuery.isLoading);
  const error =
    entityId && (financialsQuery.error || entityQuery.error)
      ? t('panels.financialSnapshot.error')
      : false;
  const isEmpty =
    entityId &&
    !loading &&
    !error &&
    financialsQuery.data &&
    entityQuery.data &&
    financialsQuery.data.data.length === 0;

  const emptyMsg = !entityId
    ? t('panels.financialSnapshot.noSelection')
    : isEmpty
      ? t('panels.financialSnapshot.noFinancials')
      : false;

  const headerToolbar =
    entityId && entityQuery.data ? (
      <div
        style={{
          padding: 'var(--space-3) var(--space-4)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
        }}
      >
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--fg-muted)' }}>
          {entityQuery.data.data.legalName ?? '—'}
        </span>
        <ScopeBadge scope={entityQuery.data.data.scopeFlag} />
      </div>
    ) : undefined;

  return (
    <PanelShell
      tabId={tabId}
      panelId={panelId}
      toolbar={headerToolbar}
      loading={loading}
      error={error}
      empty={emptyMsg}
    >
      {financialsQuery.data && financialsQuery.data.data.length > 0 && (
        <table
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
        >
          <thead
            style={{
              backgroundColor: 'var(--bg-sunken)',
              borderBottom: '1px solid var(--border-default)',
            }}
          >
            <tr>
              <Th>{t('panels.financialSnapshot.columns.period')}</Th>
              <Th align="right">{t('panels.financialSnapshot.columns.revenue')}</Th>
              <Th align="right">{t('panels.financialSnapshot.columns.ebitda')}</Th>
              <Th align="right">{t('panels.financialSnapshot.columns.netProfit')}</Th>
              <Th align="right">{t('panels.financialSnapshot.columns.employees')}</Th>
              <Th>{t('panels.financialSnapshot.columns.filing')}</Th>
            </tr>
          </thead>
          <tbody>
            {financialsQuery.data.data.map((row) => (
              <tr key={row.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <Td>{row.periodEndDate.slice(0, 4)}</Td>
                <Td align="right" className="num">
                  {formatAmount(row.revenue)}
                </Td>
                <Td align="right" className="num">
                  {formatAmount(row.ebitda)}
                </Td>
                <Td align="right" className="num">
                  {formatAmount(row.netProfit)}
                </Td>
                <Td align="right" className="num">
                  {row.employeeCount ?? '—'}
                </Td>
                <Td>{row.filingFormat ?? '—'}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </PanelShell>
  );
}

function formatAmount(value: string | null): string {
  if (value == null) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-GB', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(n);
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      style={{
        textAlign: align,
        padding: 'var(--space-2) var(--space-3)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        color: 'var(--fg-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'left',
  className,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
  className?: string;
}) {
  return (
    <td
      className={className}
      style={{
        textAlign: align,
        padding: 'var(--space-2) var(--space-3)',
        verticalAlign: 'middle',
      }}
    >
      {children}
    </td>
  );
}
